import React, { createContext, useContext, useReducer, useCallback, useEffect, useState } from "react";

const AppContext = createContext(null);

const initialState = {
  view: "map",          // map | agents | history | visits-map | reports | agent-attendance | products | users | external-users | app-alerts
  selectedAgent: null,
  historyAgent: null,
  sidebarOpen: true,
  sidebarMobileOpen: false,  // mobile sidebar toggle
  alertModalOpen: false,
  alertModalAgent: null,  // pre-fill recipient
  agentModalOpen: false,
  agentModalData: null,   // null = create, object = edit
  usersModalOpen: false,
  usersModalMode: "create", // create | edit
  usersModalData: null,     // null = create, object = edit
  notification: null,     // { type, message }
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_VIEW":          return { ...state, view: action.payload, sidebarMobileOpen: false };
    case "SELECT_AGENT":      return { ...state, selectedAgent: action.payload };
    case "SET_HISTORY_AGENT": return { ...state, historyAgent: action.payload, view: "history", sidebarMobileOpen: false };
    case "TOGGLE_SIDEBAR_MOBILE": return { ...state, sidebarMobileOpen: !state.sidebarMobileOpen };
    case "CLOSE_SIDEBAR_MOBILE": return { ...state, sidebarMobileOpen: false };
    case "OPEN_ALERT_MODAL":  return { ...state, alertModalOpen: true, alertModalAgent: action.payload ?? null };
    case "CLOSE_ALERT_MODAL": return { ...state, alertModalOpen: false, alertModalAgent: null };
    case "OPEN_AGENT_MODAL":  return { ...state, agentModalOpen: true, agentModalData: action.payload ?? null };
    case "CLOSE_AGENT_MODAL": return { ...state, agentModalOpen: false, agentModalData: null };
    case "OPEN_USERS_MODAL":  return { ...state, usersModalOpen: true, usersModalMode: action.payload?.mode || "create", usersModalData: action.payload?.data ?? null };
    case "CLOSE_USERS_MODAL": return { ...state, usersModalOpen: false, usersModalMode: "create", usersModalData: null };
    case "SET_NOTIFICATION":  return { ...state, notification: action.payload };
    default: return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("at_theme");
    if (saved) return saved;
    const hour = new Date().getHours();
    return hour >= 6 && hour < 18 ? "light" : "dark";
  });

  const setView          = useCallback(v  => dispatch({ type:"SET_VIEW", payload:v }), []);
  const selectAgent      = useCallback(a  => dispatch({ type:"SELECT_AGENT", payload:a }), []);
  const openHistory      = useCallback(a  => dispatch({ type:"SET_HISTORY_AGENT", payload:a }), []);
  const toggleSidebarMobile = useCallback(() => dispatch({ type:"TOGGLE_SIDEBAR_MOBILE" }), []);
  const closeSidebarMobile = useCallback(() => dispatch({ type:"CLOSE_SIDEBAR_MOBILE" }), []);
  const openAlertModal   = useCallback(a  => dispatch({ type:"OPEN_ALERT_MODAL", payload:a }), []);
  const closeAlertModal  = useCallback(()  => dispatch({ type:"CLOSE_ALERT_MODAL" }), []);
  const openAgentModal   = useCallback(a  => dispatch({ type:"OPEN_AGENT_MODAL", payload:a }), []);
  const closeAgentModal  = useCallback(()  => dispatch({ type:"CLOSE_AGENT_MODAL" }), []);
  const openUsersModal   = useCallback((mode, data) => dispatch({ type:"OPEN_USERS_MODAL", payload:{ mode, data } }), []);
  const closeUsersModal  = useCallback(() => dispatch({ type:"CLOSE_USERS_MODAL" }), []);
  const notify           = useCallback((type, message) => {
    dispatch({ type:"SET_NOTIFICATION", payload:{ type, message } });
    setTimeout(() => dispatch({ type:"SET_NOTIFICATION", payload:null }), 3500);
  }, []);

  useEffect(() => {
    localStorage.setItem("at_theme", theme);
    document.body.classList.toggle("theme-light", theme === "light");
  }, [theme]);

  return (
    <AppContext.Provider value={{ ...state, setView, selectAgent, openHistory, toggleSidebarMobile, closeSidebarMobile, openAlertModal, closeAlertModal, openAgentModal, closeAgentModal, openUsersModal, closeUsersModal, notify, theme, setTheme }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
